import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { ContactDetail } from '../../components/ContactDetailsModal';
import { Loader } from '../../components/Loader';
import { MatchEditor } from '../../components/MatchEditor';
import { WithPageViewModes } from '../../components/WithPageViewModes';
import { ROUTE_VIEW_MODE, VIEW_MODE } from '../../consts/enums';
import { PERMISSIONS } from '../../consts/permissions';
import { useGetMatch } from '../../hooks/useMatch';
import { useGetTalent } from '../../hooks/useTalent';

type MatchEditPageProps = {
  viewMode: ViewMode,
};

const MatchEditPageComponent = ({
  viewMode,
} : MatchEditPageProps): JSX.Element => {
  const { positionId = '', matchId = '' } = useParams();
  const [talent, setTalent] = useState<FormattedTalentResponse>();
  const [saveDraftEnabled, setSaveDraftEnabled] = useState<boolean>(false);
  const {
    match, isLoading, refetch: refetchMatch, isFetching,
  } = useGetMatch(positionId, matchId, (matchData) => setTalent(matchData.talent));
  const [latestVersion, setLatestVersion] = useState('0');
  const { refetch } = useGetTalent(match?.talent?.talentId ||
    '', (talentData) => {
    setTalent(talentData);
  }, true);
  // todo: handle errors when loading data

  const handleMatchSaved = () => {
    refetchMatch();
  };

  const handleApplyChanges = () => {
    refetch();
    setSaveDraftEnabled(true);
  };

  const handleDisableSaveDraft = () => {
    setSaveDraftEnabled(false);
  };

  if (isLoading || isFetching) {
    return (
      <Loader top="45%" left="45%" />
    );
  }

  if (!match) {
    // todo: change it
    return <div>Error: No Data info</div>;
  }

  const updateContactDetailsHandler = (contactDetails: ContactDetail) => {
    setTalent((prevstate: FormattedTalentResponse | undefined)
    :FormattedTalentResponse | undefined => {
      if (prevstate) {
        const emailsCopy = [...contactDetails.emails || []];
        const phonesCopy = [...contactDetails.phones || []];

        return {
          ...prevstate,
          emails: emailsCopy,
          phoneNumbers: phonesCopy,
        };
      }

      return {} as FormattedTalentResponse;
    });
  };

  const currentTalentVersion = (talent?.version || match?.talent?.version);
  const hasNewerTalentVersion = () => {
  //  0: version strings are equal
  //  > 0: version current is greater than latestVersion
  //  < 0: version latestVersion is greater than current

    const result = currentTalentVersion?.localeCompare(
      latestVersion,
      undefined,
      { numeric: true, sensitivity: 'base' },
    );

    return result < 0;
  };

  return (
    <MatchEditor
      viewMode={ viewMode }
      accountId={ match.accountId }
      positionId={ positionId }
      matchId={ matchId }
      match={ match }
      talent={ talent || match.talent }
      setLatestVersion={ setLatestVersion }
      isShowNotification={ hasNewerTalentVersion() }
      onApplyChanges={ handleApplyChanges }
      isLatestUpdate={ currentTalentVersion > match.talent.version }
      onSaveMatch={ handleMatchSaved }
      latestTalentVersion={ latestVersion }
      onContactDetailsUpdate={ updateContactDetailsHandler }
      saveDraftEnabled={ saveDraftEnabled }
      handleDisableSaveDraft={ handleDisableSaveDraft }
    />
  );
};

export const MatchEditPage = WithPageViewModes(MatchEditPageComponent, [
  {
    routeParam: ROUTE_VIEW_MODE.VIEW,
    viewMode: VIEW_MODE.READ_ONLY,
    permissions: [PERMISSIONS.MATCH_VIEW],
  },
  {
    routeParam: ROUTE_VIEW_MODE.EDIT,
    viewMode: VIEW_MODE.EDITABLE,
    permissions: [PERMISSIONS.MATCH_EDIT],
  },
]);
